import api from './api'
import type {
  NotionAuthInitResponse,
  NotionConnectionStatusResponse,
  NotionConnectionSuccessResponse,
  NotionAuthCallbackRequest,
} from '@/schemas/notion-integration'
import type {
  NotionPageListResponse,
  NotionPageChildrenResponse,
} from '@/schemas/notion-sync'
import type { AxiosRequestConfig } from 'axios'

export const notionIntegrationService = {
  /**
   * Initiate Notion OAuth flow
   * Returns the auth URL to redirect the user to Notion consent screen
   */
  async initiateAuth(
    orgId: string,
    config?: AxiosRequestConfig
  ): Promise<NotionAuthInitResponse> {
    const response = await api.post<NotionAuthInitResponse>(
      `/organizations/${orgId}/integrations/notion/auth`,
      {},
      config
    )
    return response.data
  },

  /**
   * Handle Notion OAuth callback
   * Exchanges the authorization code for tokens and creates the connection
   */
  async handleCallback(
    orgId: string,
    data: NotionAuthCallbackRequest,
    config?: AxiosRequestConfig
  ): Promise<NotionConnectionSuccessResponse> {
    const response = await api.post<NotionConnectionSuccessResponse>(
      `/organizations/${orgId}/integrations/notion/callback`,
      data,
      config
    )
    return response.data
  },

  /**
   * Get Notion connection status for an organization
   */
  async getStatus(
    orgId: string,
    config?: AxiosRequestConfig
  ): Promise<NotionConnectionStatusResponse> {
    const response = await api.get<NotionConnectionStatusResponse>(
      `/organizations/${orgId}/integrations/notion/status`,
      config
    )
    return response.data
  },

  /**
   * Disconnect Notion account from organization
   */
  async disconnect(orgId: string, config?: AxiosRequestConfig): Promise<void> {
    await api.delete(`/organizations/${orgId}/integrations/notion`, config)
  },

  /**
   * Search pages in Notion workspace
   * Used by the page picker component
   */
  async searchPages(
    orgId: string,
    query?: string,
    config?: AxiosRequestConfig
  ): Promise<NotionPageListResponse> {
    const response = await api.get<NotionPageListResponse>(
      `/organizations/${orgId}/integrations/notion/pages/search`,
      {
        ...config,
        params: query ? { query } : undefined,
      }
    )
    return response.data
  },

  /**
   * List children pages of a Notion page
   * Shows child pages that can be selected for sync
   */
  async listPageChildren(
    orgId: string,
    pageId: string,
    config?: AxiosRequestConfig
  ): Promise<NotionPageChildrenResponse> {
    const response = await api.get<NotionPageChildrenResponse>(
      `/organizations/${orgId}/integrations/notion/pages/${pageId}/children`,
      config
    )
    return response.data
  },
}
